import React, { useState } from "react";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import EmphasizedMessage from "../EmphasizedMessage";
import MentorCard, { ASSIGNED_NOTE } from "../MentorCard";
import { ASSIGN_MODES } from "./constants";
import styles from "./MentorAssignPanel.module.css";

/**
 * A picked mentor's check that did not come back free: a box like the screen's
 * other refusals, the class time first on each line and the reason after it.
 */
const Unavailable = ({ mentor }) =>
  mentor?.occupiedTimings?.length > 0 ? (
    <div className={styles.unavailable} role="alert">
      <SvgIcons name="exclamation_circle_filled" className={styles.unavailableIcon} width="16" height="16" />
      <div className={styles.unavailableBody}>
        <p className={styles.unavailableTitle}>{mentor?.name} isn&rsquo;t available for this schedule</p>
        <ul className={styles.unavailableList}>
          {mentor?.occupiedTimings?.map((timing) => (
            <li key={`${timing?.day}-${timing?.from}`} className={styles.unavailableRow}>
              <span className={styles.unavailableTime}>
                {timing?.dayLabel} {timing?.fromLabel ?? timing?.from} IST
              </span>
              <span className={styles.unavailableReason}>
                <EmphasizedMessage text={timing?.reasonLabel} />
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  ) : null;

/**
 * A search result: the name, then the facts the best-matched list shows for each
 * mentor (rating, tier, shift), so a searched pick is chosen on the same footing.
 * The rating carries the star the mentor cards use for it.
 */
const SearchResultFacts = ({ mentor }) => (
  <span className={styles.resultFacts}>
    <span className={styles.resultFact} title="Rating">
      <SvgIcons name="star_filled" width="14" height="14" />
      {mentor?.rating ?? "Not rated"}
    </span>
    {mentor?.tier && <span className={styles.resultFact}>Tier {mentor?.tier}</span>}
    {mentor?.shift && (
      <span className={styles.resultFact}>
        <SvgIcons name="video_icon" width="14" height="14" />
        {mentor?.shift}
      </span>
    )}
  </span>
);

/** Calendar conflicts and leave still stand against a free pick; named, not blocking. */
const PickWarnings = ({ mentor }) =>
  mentor?.conflictCount > 0 || mentor?.leaves?.length > 0 ? (
    <ul className={styles.warnings}>
      {mentor?.conflictCount > 0 && (
        <li>
          {mentor?.conflictCount} calendar conflict{mentor?.conflictCount > 1 ? "s" : ""} at these
          times. You&rsquo;ll see them when you add the batch.
        </li>
      )}
      {mentor?.leaves?.map((leave) => (
        <li key={`${leave?.from}-${leave?.to}`}>
          On leave {leave?.from} to {leave?.to} (IST)
        </li>
      ))}
    </ul>
  ) : null;

/**
 * The Mentor section body, as the design draws it: one list, three groups
 * (R-05-01) — the known mentor (demo, else current, else past, tagged by the
 * backend), the best-matched mentors, and search.
 *
 * Choosing IS assigning (R-05-10): there is no separate assign control. A free
 * known mentor is chosen with its Select button; a best-matched one from the
 * popup; a searched one by picking it from the results. Whatever was chosen
 * reads "This mentor will be assigned directly to the batch". The known mentor
 * free → the other two groups fold under "Other options"; occupied or absent →
 * they are shown open. `busy` disables every choice while the list is fetched —
 * choosing on a stale answer is the state R-05-01 rules out.
 *
 * @param {{ label?: string, knownMentor?: object|null, showKnown?: boolean,
 *           busy?: boolean,
 *           mode: "known"|"bestMatched"|"search"|null, onModeChange: (mode: string) => void,
 *           best: {count: number, pick?: object|null, onViewMentors: () => void},
 *           search: {query: string, onQueryChange: (query: string) => void,
 *             results?: {id: number, name: string, rating?: number|null,
 *               tier?: string, shift?: string}[],
 *               — undefined before a search has run, [] when a search found nothing,
 *             pickId?: number|null, onPick?: (id: number) => void,
 *             pick?: object|null, isChecking?: boolean, isSearching?: boolean,
 *             error?: string},
 *           assignment: {assigned: boolean} }} props
 */
const MentorAssignPanel = ({
  label = "Choose any one method to assign",
  knownMentor,
  showKnown = true,
  busy,
  mode,
  onModeChange,
  best,
  search,
  assignment,
}) => {
  const [openedByUser, setOpenedByUser] = useState(false);
  const known = showKnown ? knownMentor : null;
  const knownAvailable = known?.status === "available";
  /* A selection made outside the accordion must stay visible — edit mounts with
     a mode already chosen. */
  const otherOpen = openedByUser || mode === ASSIGN_MODES.BEST || mode === ASSIGN_MODES.SEARCH;
  const isBest = mode === ASSIGN_MODES.BEST;
  const isSearch = mode === ASSIGN_MODES.SEARCH;
  const bestPicked = isBest && !!best?.pick;
  const pickedName =
    search?.pick?.name ?? search?.results?.find((mentor) => mentor?.id === search?.pickId)?.name;
  /* A picked name stands in the box, as the design shows it; typing again starts
     a new search and drops the pick. */
  const searchValue = search?.pickId && pickedName ? pickedName : (search?.query ?? "");
  const showResults = isSearch && !search?.pickId && !!search?.query?.trim?.();

  const bestRow = (
    <div className={isBest ? styles.rowSelected : styles.row}>
      <label className={bestPicked ? styles.rowHeaderDivided : styles.rowHeader}>
        <input
          type="radio"
          className={styles.radio}
          checked={isBest}
          disabled={busy || !best?.count}
          onChange={() => onModeChange?.(ASSIGN_MODES.BEST)}
        />
        <span className={styles.rowIcon}>
          <SvgIcons name="person_check_filled" width="20" height="20" />
        </span>
        <span className={styles.rowText}>
          <span className={styles.rowTitle}>
            {!best?.count && "No other mentors are available for this schedule"}
            {best?.count > 0 &&
              (bestPicked
                ? "We’ve fetched suggested mentors based on the best match."
                : `We’ve fetched ${best?.count} suggested ${best?.count === 1 ? "mentor" : "mentors"} based on the best match.`)}
            {best?.count > 0 && <SvgIcons name="sparkles_filled" width="16" height="16" />}
          </span>
          {best?.count > 0 && (
            <span className={styles.rowSubtitle}>
              {bestPicked ? (
                <>
                  <span>
                    Selected mentor: <span className={styles.pickedName}>{best?.pick?.name}</span>
                  </span>
                  <button type="button" className={styles.link} disabled={busy} onClick={best?.onViewMentors}>
                    Edit
                    <SvgIcons name="edit_icon" width="12" height="12" />
                  </button>
                </>
              ) : (
                <>
                  Select a mentor to assign this batch
                  <button type="button" className={styles.link} disabled={busy} onClick={best?.onViewMentors}>
                    View mentors
                    <SvgIcons name="external_link" width="16" height="16" />
                  </button>
                </>
              )}
            </span>
          )}
        </span>
      </label>
      {bestPicked && <PickWarnings mentor={best?.pick} />}
      {bestPicked && assignment?.assigned && <p className={styles.note}>{ASSIGNED_NOTE}</p>}
    </div>
  );

  const searchRow = (
    <div className={isSearch ? styles.rowSelected : styles.row}>
      <label className={styles.rowHeader}>
        <input
          type="radio"
          className={styles.radio}
          checked={isSearch}
          disabled={busy}
          onChange={() => onModeChange?.(ASSIGN_MODES.SEARCH)}
        />
        <span className={styles.rowIcon}>
          <SvgIcons name="pick_manually" width="20" height="20" />
        </span>
        <span className={styles.rowTitle}>
          {isSearch ? "Search mentor" : "Search and assign to mentor directly"}
        </span>
      </label>
      {isSearch && (
        <>
          <span className={styles.searchShell}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Type and search for mentors"
              value={searchValue}
              onChange={(event) => search?.onQueryChange?.(event?.target?.value)}
            />
            <SvgIcons name="search_field_icon" className={styles.searchIcon} width="20" height="20" />
            {showResults && (
              <span className={styles.results} role="listbox">
                {search?.isSearching && <span className={styles.resultsEmpty}>Searching…</span>}
                {/* The failure belongs beside the box that failed. */}
                {!search?.isSearching && search?.error && (
                  <span className={styles.resultsError} role="alert">
                    <EmphasizedMessage text={search?.error} />
                  </span>
                )}
                {!search?.isSearching && !search?.error && search?.results?.length === 0 && (
                  <span className={styles.resultsEmpty}>
                    No mentors match that search. A mentor who is not mapped to this course will not appear.
                  </span>
                )}
                {!search?.isSearching &&
                  search?.results?.map((mentor) => (
                    <label key={mentor?.id} className={styles.result}>
                      <input
                        type="radio"
                        name="searched-mentor"
                        className={styles.resultRadio}
                        disabled={busy}
                        checked={search?.pickId === mentor?.id}
                        onChange={() => search?.onPick?.(mentor?.id)}
                      />
                      <span className={styles.resultText}>
                        <span className={styles.resultName}>{mentor?.name}</span>
                        <SearchResultFacts mentor={mentor} />
                      </span>
                    </label>
                  ))}
              </span>
            )}
          </span>
          {search?.isChecking && <p className={styles.note}>Checking this mentor&rsquo;s availability…</p>}
          {!search?.isChecking && search?.pick && (
            <>
              <Unavailable mentor={search?.pick} />
              <PickWarnings mentor={search?.pick} />
              {assignment?.assigned && <p className={styles.noteRuled}>{ASSIGNED_NOTE}</p>}
            </>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className={styles.wrap}>
      <div className={styles.label}>{label}</div>

      {known && (
        <MentorCard
          mentor={known}
          variant="option"
          selected={mode === ASSIGN_MODES.KNOWN}
          assigned={assignment?.assigned}
          disabled={busy}
          onSelect={() => onModeChange?.(ASSIGN_MODES.KNOWN)}
        />
      )}

      {known && knownAvailable ? (
        <>
          <button
            type="button"
            className={styles.otherOptions}
            aria-expanded={otherOpen}
            onClick={() => setOpenedByUser(!otherOpen)}
          >
            Other options
            <SvgIcons
              name="chevron_down"
              className={otherOpen ? styles.chevronOpen : undefined}
              width="20"
              height="20"
            />
          </button>
          {otherOpen && bestRow}
          {otherOpen && searchRow}
        </>
      ) : (
        <>
          {bestRow}
          {searchRow}
        </>
      )}
    </div>
  );
};

export default MentorAssignPanel;
